/**
 * GLSL for the Glyph visualizer — one fullscreen shader quad driving all 26
 * letter effects over a selectable base visual and a selectable colour palette.
 *
 * `uVisual` (number row 1–7) picks the base field: 0 oscilloscope, 1 tunnel,
 * 2 oil, 3 starfield, 4 metaballs (lava lamp), 5 voronoi, 6 curl flow. Each base
 * also owns a +/- modifier `uVMod<Visual>` (0..1, 0.5 neutral) scrubbed live by
 * holding its number key (oscilloscope → frequency, tunnel → speed, oil → swirl,
 * starfield → density, metaballs → blobs, voronoi → cells, curlFlow → flow).
 * `uPaletteType` (number row 8–0) picks the colour-palette type and `uBaseHue` its
 * base hue; together they drive `palette()`, recolouring every base and effect at once.
 *
 * Every effect is one branch gated by a uniform whose value is the paramBus
 * intensity (0 off, 1 normal, ~1.8 Shift-amplified). Each also reads a per-effect
 * modifier `uMod<Effect>` (0..1, 0.5 neutral) scrubbed live by +/-, mapped to a
 * fun parameter (kaleido → segments, fracture → shards, glitch → intensity, …).
 * The pipeline is:
 *   1. uv-domain transforms (yaw, twist, zoom, quake, jello, explode, distort,
 *      warp, underwater, ripple, fracture, melt, crystallize, glitch, pixellate)
 *   2. base visual field sample via sampleBase (K adds mirror segments)
 *   3. colour effects (invert, alien, x-ray, hue, noise, ocean, lens flare,
 *      bloom, vignette, strobe) + the live-mode beat pulse
 * Several effects lock to the Space-bar tempo (`uBeat`): explode bursts, ripple
 * rings, zoom punches and bloom swells all pulse on the downbeat, alongside strobe.
 *
 * Written for three's ShaderMaterial (GLSL ES 1.00: gl_FragColor). Avoids GLSL
 * reserved words (e.g. no identifier named `active`).
 */

export const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

export const fragmentShader = /* glsl */ `
  precision highp float;

  varying vec2 vUv;
  uniform float uTime;
  uniform vec2  uResolution;

  // Letter effects (A–Z).
  uniform float uAlien, uBloom, uCrystallize, uDistort, uExplode, uFracture, uGlitch;
  uniform float uHue, uInvert, uJello, uKaleido, uLensflare, uMelt, uNoise;
  uniform float uOcean, uPixellate, uQuake, uRipple, uStrobe, uTwist, uUnderwater;
  uniform float uVignette, uWarp, uXray, uYaw, uZoom;
  uniform float uBlackout, uWhiteout;

  // Per-effect modifiers (+/- scrub; 0.5 neutral).
  uniform float uModAlien, uModBloom, uModCrystallize, uModDistort, uModExplode, uModFracture, uModGlitch;
  uniform float uModHue, uModInvert, uModJello, uModKaleido, uModLensflare, uModMelt, uModNoise;
  uniform float uModOcean, uModPixellate, uModQuake, uModRipple, uModStrobe, uModTwist, uModUnderwater;
  uniform float uModVignette, uModWarp, uModXray, uModYaw, uModZoom;
  uniform float uModBlackout, uModWhiteout;

  // Per-base-visual modifiers (+/- scrub while a number key is held; 0.5 neutral).
  uniform float uVModOscilloscope, uVModTunnel, uVModOil, uVModStarfield;
  uniform float uVModMetaballs, uVModVoronoi, uVModCurlFlow;

  // Global camera transform (persistent). Zoom is a uv scale here; global speed
  // is folded into uTime by the renderer, so it needs no uniform of its own.
  uniform float uGlobalZoom; // 0..1, 0.5 neutral (1x)

  // Tempo.
  uniform float uBeat; // 0..1 beat envelope
  uniform float uLive; // 1 in live-performance mode

  // Base-visual selector (0 oscilloscope · 1 tunnel · 2 oil · 3 starfield ·
  // 4 metaballs · 5 voronoi · 6 curl flow).
  uniform float uVisual;
  // Palette type (0 analogous · 1 complementary · 2 spectrum) and its base hue
  // (0..1 around the wheel).
  uniform float uPaletteType;
  uniform float uBaseHue;

  // Hidden combos (0 idle, 1 firing). Glyph = one-off supernova sweep;
  // Meltdown = M+E+L+T chord surge while held.
  uniform float uComboGlyph, uComboMeltdown;

  float hash(float n) { return fract(sin(n) * 43758.5453123); }
  float hash2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise2(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    float a = hash2(i), b = hash2(i + vec2(1, 0));
    float c = hash2(i + vec2(0, 1)), d = hash2(i + vec2(1, 1));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 4; i++) { v += a * noise2(p); p *= 2.0; a *= 0.5; }
    return v;
  }
  mat2 rot(float a) { return mat2(cos(a), -sin(a), sin(a), cos(a)); }
  float luma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }

  vec3 hsv2rgb(vec3 c) {
    vec3 p = abs(fract(c.xxx + vec3(0.0, 2.0 / 3.0, 1.0 / 3.0)) * 6.0 - 3.0);
    return c.z * mix(vec3(1.0), clamp(p - 1.0, 0.0, 1.0), c.y);
  }

  // Hue-relationship palette: every type derives its colours from a single base
  // hue (uBaseHue, shifted live by +/-). The scalar t sweeps position within the
  // ramp; each type maps it to a hue.
  //   0 analogous · 1 complementary · 2 spectrum
  // Spectrum keeps the original IQ cosine rainbow, merely rotated by uBaseHue, so
  // at base hue 0 the default scene looks unchanged.
  vec3 palette(float t) {
    int ty = int(uPaletteType + 0.5);
    if (ty == 2) { // Spectrum — original rainbow, rotational start = uBaseHue
      vec3 d = vec3(0.00, 0.33, 0.67);
      return 0.5 + 0.5 * cos(6.28318 * ((t + uBaseHue) + d));
    }

    float tt = fract(t);
    float h = uBaseHue;
    if (ty == 0) {        // Analogous — centre hue ± ~30°
      h = uBaseHue + (tt - 0.5) * (60.0 / 360.0);
    } else {              // Complementary — base ↔ base+180°, dwelling on each
      h = uBaseHue + smoothstep(0.35, 0.65, tt) * 0.5;
    }
    return hsv2rgb(vec3(fract(h), 0.85, 0.95));
  }

  // Aspect-correct uv into 0..1. Bases work in this space so effects (which warp
  // uv) and the kaleido fold stay coordinate-consistent across every visual.
  vec2 aspect(vec2 uv) {
    vec2 p = uv - 0.5;
    p.x *= uResolution.x / max(uResolution.y, 1.0);
    return p;
  }

  // Visual 0 — oscilloscope: a vibrant neon Lissajous trace glowing on a dark
  // field. The +/- modifier tunes the frequency ratio (figure complexity). The
  // brightness is the distance to the nearest curve point, so the line stays a
  // steady glow rather than strobing with the beat.
  vec3 fieldOscilloscope(vec2 uv, float ft) {
    vec2 p = aspect(uv);
    float freq = mix(2.0, 5.0, uVModOscilloscope); // 0.5 ≈ 3.5
    float best = 1e9;
    const int N = 160;
    for (int i = 0; i < N; i++) {
      float t = float(i) / float(N) * 6.28318;
      vec2 c = 0.46 * vec2(sin(freq * t + ft * 0.7),
                           sin((freq + 1.0) * t + ft * 0.5 + 1.5708));
      vec2 dd = p - c;
      best = min(best, dot(dd, dd));
    }
    float d = sqrt(best);
    float core  = smoothstep(0.018, 0.0, d);  // crisp near-white trace
    float bloom = exp(-d * 6.0);              // soft coloured halo fills the field
    vec3 tint = palette(fract(0.5 + length(p) * 0.6 + ft * 0.08));
    vec3 col = tint * bloom;                   // vibrant glow
    col += mix(tint, vec3(1.0), 0.7) * core;   // hot core rides on top
    return col;
  }

  // Visual 1 — tunnel / warp-speed: rings streaking through a dark radial core.
  // The +/- modifier tunes the fly-through speed + ring density.
  vec3 fieldTunnel(vec2 uv, float ft) {
    vec2 p = aspect(uv);
    float r = length(p);
    float a = atan(p.y, p.x);
    float speed = mix(0.3, 1.1, uVModTunnel); // 0.5 ≈ the original 0.6
    float depth = 0.35 / (r + 0.08) + ft * speed;
    float rings = 0.5 + 0.5 * sin(depth * 6.0);
    float stripes = 0.5 + 0.5 * sin(a * 8.0 + depth * 2.0);
    vec3 col = palette(depth * 0.05 + a * 0.15 + (rings * 0.6 + stripes * 0.4) * 0.3);
    col *= smoothstep(0.0, 0.15, r);
    col *= 0.4 + 0.6 * rings;
    return col;
  }

  // Visual 2 — oil: domain-warped fbm turbulence, marbled like an oil slick.
  // The +/- modifier tunes the swirl (domain-warp strength).
  vec3 fieldOil(vec2 uv, float ft) {
    vec2 p = aspect(uv) * 3.0 + 4.0;
    float swirl = mix(2.5, 5.5, uVModOil); // 0.5 ≈ the original 4.0
    vec2 q = vec2(fbm(p + vec2(0.0, ft * 0.1)), fbm(p + vec2(5.2, 1.3 - ft * 0.1)));
    vec2 s = vec2(fbm(p + swirl * q + vec2(1.7, 9.2)), fbm(p + swirl * q + vec2(8.3, 2.8)));
    float f = fbm(p + swirl * s + ft * 0.05);
    vec3 col = mix(palette(0.1 + f), palette(0.6 + length(q)), clamp(f * f * 2.0, 0.0, 1.0));
    return col * (0.6 + 0.6 * f);
  }

  // Visual 3 — starfield: parallax layers of twinkling points on black + faint nebula.
  // The +/- modifier tunes the star density (how many cells light up).
  vec3 fieldStarfield(vec2 uv, float ft) {
    vec2 p = aspect(uv);
    vec3 col = vec3(0.0);
    float thresh = mix(0.7, 0.22, uVModStarfield); // 0.5 ≈ the original 0.55; higher mod = more stars
    for (int i = 0; i < 3; i++) {
      float fl = float(i);
      vec2 sp = p * (8.0 + fl * 10.0) + vec2(ft * (0.3 + fl * 0.2), ft * 0.1);
      vec2 cell = floor(sp);
      vec2 fc = fract(sp) - 0.5;
      float rnd = hash2(cell + fl * 17.0);
      vec2 off = (vec2(hash2(cell + 3.0), hash2(cell + 7.0)) - 0.5) * 0.7;
      float star = smoothstep(0.12, 0.0, length(fc - off)) * step(thresh, rnd);
      float tw = 0.6 + 0.4 * sin(ft * 3.0 + rnd * 40.0);
      col += palette(rnd + fl * 0.2) * star * tw * (1.0 - fl * 0.25);
    }
    col += palette(fbm(p * 2.0 + ft * 0.05) + 0.5) * 0.06;
    return col;
  }

  // Visual 4 — metaballs / lava lamp: merging organic blobs. Modifier = blob count.
  vec3 fieldMetaballs(vec2 uv, float ft) {
    vec2 p = aspect(uv);
    float n = floor(mix(3.0, 14.0, uVModMetaballs));
    float fieldv = 0.0;
    const int MAX_B = 14;
    for (int i = 0; i < MAX_B; i++) {
      if (float(i) >= n) break;
      float fi = float(i);
      // Slow the blob drift / radius pulse (Lava Lamp reads calmer). Colour drift
      // below still keys off ft, so hue cycling stays independent of the motion.
      float mt = ft * 0.55;
      vec2 c = 0.55 * vec2(sin(mt * (0.30 + fi * 0.11) + fi * 2.1),
                           cos(mt * (0.27 + fi * 0.13) + fi * 1.3));
      float r = 0.13 + 0.05 * sin(mt + fi);
      vec2 d = p - c;
      fieldv += r * r / (dot(d, d) + 0.0008);
    }
    float m = smoothstep(0.8, 1.6, fieldv);
    vec3 col = palette(fieldv * 0.15 + ft * 0.03) * m;
    col += palette(0.5 + ft * 0.05) * smoothstep(0.9, 1.15, fieldv) * 0.35; // rim
    return col;
  }

  // Visual 5 — Voronoi / cellular: shifting stained-glass cells. Modifier = cell count.
  vec3 fieldVoronoi(vec2 uv, float ft) {
    vec2 p = aspect(uv);
    float cells = mix(3.0, 12.0, uVModVoronoi);
    vec2 sp = p * cells + cells;                 // keep the grid positive
    vec2 ip = floor(sp);
    vec2 fp = fract(sp);
    float best = 1e9, second = 1e9;
    vec2 bestCell = ip;
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 g = vec2(float(x), float(y));
        vec2 o = vec2(hash2(ip + g), hash2(ip + g + vec2(19.0, 7.0)));
        o = 0.5 + 0.5 * sin(ft * 0.6 + 6.28318 * o);            // drift the seeds
        vec2 dd = g + o - fp;
        float d = dot(dd, dd);
        if (d < best) { second = best; best = d; bestCell = ip + g; }
        else if (d < second) { second = d; }
      }
    }
    float edge = smoothstep(0.0, 0.05, sqrt(second) - sqrt(best)); // dark leading
    vec3 col = palette(hash2(bestCell) + ft * 0.02);
    return col * (0.3 + 0.7 * edge);
  }

  // Visual 6 — curl-noise flow field: streaming filaments. Modifier = filament density.
  vec3 fieldCurlFlow(vec2 uv, float ft) {
    vec2 p = aspect(uv) * 2.0;
    float density = mix(4.0, 12.0, uVModCurlFlow);
    float e = 0.05;
    vec2 flow = vec2(fbm(p + vec2(0.0, e) + ft * 0.1) - fbm(p - vec2(0.0, e) + ft * 0.1),
                     fbm(p - vec2(e, 0.0) + ft * 0.1) - fbm(p + vec2(e, 0.0) + ft * 0.1));
    float ang = atan(flow.y, flow.x);
    float phase = p.x * flow.x + p.y * flow.y;
    float streak = pow(0.5 + 0.5 * sin(ang * density + phase * 6.0 - ft * 2.0), 3.0);
    float mag = length(flow) * 6.0;
    return palette(ang / 6.28318 + 0.5 + ft * 0.03) * streak * (0.4 + mag);
  }

  // Kaleidoscopic mirror-fold in aspect space — lets the K effect add mirror
  // symmetry to any base visual.
  vec2 kaleidoFold(vec2 uv, float seg) {
    vec2 p = aspect(uv);
    float a = atan(p.y, p.x);
    float r = length(p);
    float wedge = 6.28318 / seg;
    a = abs(mod(a, wedge) - wedge * 0.5);
    p = vec2(cos(a), sin(a)) * r;
    p.x /= uResolution.x / max(uResolution.y, 1.0);
    return p + 0.5;
  }

  vec3 hueRotate(vec3 c, float ang) {
    const mat3 toYIQ = mat3(0.299, 0.596, 0.211, 0.587, -0.274, -0.523, 0.114, -0.322, 0.312);
    const mat3 toRGB = mat3(1.0, 1.0, 1.0, 0.956, -0.272, -1.106, 0.621, -0.647, 1.703);
    vec3 yiq = toYIQ * c;
    float cs = cos(ang), sn = sin(ang);
    yiq = vec3(yiq.x, yiq.y * cs - yiq.z * sn, yiq.y * sn + yiq.z * cs);
    return toRGB * yiq;
  }

  // Globals set in main().
  float SEG;
  float BT;   // base sample time

  // Dispatch to the active base visual. Every base gets an optional kaleidoscopic
  // fold when the K effect (uKaleido) is on, so "extra mirror segments" reads on
  // every visual.
  vec3 sampleBase(vec2 uv, float ft) {
    int v = int(uVisual + 0.5);
    if (uKaleido > 0.0) uv = kaleidoFold(uv, SEG);
    if (v == 0) return fieldOscilloscope(uv, ft);
    if (v == 1) return fieldTunnel(uv, ft);
    if (v == 2) return fieldOil(uv, ft);
    if (v == 3) return fieldStarfield(uv, ft);
    if (v == 4) return fieldMetaballs(uv, ft);
    if (v == 5) return fieldVoronoi(uv, ft);
    return fieldCurlFlow(uv, ft); // 6
  }

  vec3 sampleField(vec2 uv) {
    return sampleBase(uv, BT);
  }

  void main() {
    float T = uTime;                             // dials removed — fixed 1x timebase

    // Kaleido segment count is the K modifier (fewer/more mirror wedges).
    SEG = 6.0 + (uKaleido > 0.0 ? mix(2.0, 20.0, uModKaleido) * uKaleido : 0.0);
    BT = T;

    vec2 base = vUv;
    vec2 uv = base;

    // Global zoom — a persistent camera dolly before any effect transform.
    // 0.5 neutral (1x); 1.0 pushes in 4x, 0.0 pulls back to 1/4. Log-symmetric.
    float gz = pow(4.0, (uGlobalZoom - 0.5) * 2.0);
    uv = (uv - 0.5) / gz + 0.5;

    if (uYaw > 0.0) { vec2 c = uv - 0.5; c = rot(T * mix(0.15, 1.2, uModYaw) * uYaw) * c; uv = c + 0.5; }
    if (uTwist > 0.0) { vec2 c = uv - 0.5; c = rot(length(c) * mix(2.0, 14.0, uModTwist) * uTwist) * c; uv = c + 0.5; }
    // Zoom punches in on each downbeat (tempo-synced), depth from its modifier.
    if (uZoom > 0.0) { float s = 1.0 + mix(0.08, 0.6, uModZoom) * uZoom * uBeat; uv = (uv - 0.5) / s + 0.5; }
    if (uQuake > 0.0) uv += (vec2(hash(floor(T * 30.0)), hash(floor(T * 30.0) + 7.0)) - 0.5) * mix(0.015, 0.09, uModQuake) * uQuake;

    // Jello — springy elastic wobble; modifier sets the ripple frequency.
    if (uJello > 0.0) {
      float freq = mix(6.0, 26.0, uModJello);
      vec2 c = uv - 0.5;
      float env = 0.02 * uJello;
      uv.x += env * sin(c.y * freq + T * 7.0) * (0.7 + 0.3 * sin(T * 3.0));
      uv.y += env * sin(c.x * freq - T * 7.0) * (0.7 + 0.3 * cos(T * 3.0));
    }

    // Explode — radial burst that peaks on the beat; modifier sets magnitude.
    if (uExplode > 0.0) {
      vec2 c = uv - 0.5;
      uv += c * mix(0.3, 1.4, uModExplode) * min(uExplode, 1.5) * uBeat;
    }

    if (uDistort > 0.0) { float g = mix(0.4, 2.2, uModDistort); uv.x += 0.03 * g * uDistort * sin(uv.y * 18.0 + T * 2.0); uv.y += 0.03 * g * uDistort * cos(uv.x * 16.0 - T * 2.0); }
    if (uWarp > 0.0) uv += 0.06 * uWarp * mix(0.3, 2.0, uModWarp) * vec2(sin(uv.y * 8.0 + T), cos(uv.x * 8.0 - T));
    if (uUnderwater > 0.0) { float g = mix(0.3, 2.0, uModUnderwater); uv.x += 0.015 * g * uUnderwater * sin(uv.y * 24.0 + T * 1.5); uv.y += 0.015 * g * uUnderwater * sin(uv.x * 20.0 + T * 1.2); }

    // Ripple — concentric rings whose amplitude pulses on the beat; modifier = wavelength.
    if (uRipple > 0.0) {
      vec2 c = uv - 0.5;
      float d = length(c);
      float freq = mix(20.0, 80.0, uModRipple);
      float disp = sin(d * freq - T * 6.0) * (0.008 + 0.02 * uBeat);
      uv += (c / max(d, 1e-4)) * disp * min(uRipple, 1.5);
    }

    if (uFracture > 0.0) { float cells = mix(4.0, 40.0, uModFracture); vec2 cell = floor(uv * cells); uv += (vec2(hash2(cell), hash2(cell + 3.0)) - 0.5) * 0.06 * uFracture; }

    if (uMelt > 0.0) {
      float len = mix(0.03, 0.12, uModMelt);
      float drip = hash2(vec2(floor(uv.x * 80.0), 1.0));
      uv.y += len * uMelt * drip * (0.5 + 0.5 * sin(T * 2.0 + uv.x * 40.0));
      uv.y += 0.03 * uMelt * sin(uv.x * 25.0 - T * 3.0);
    }

    // Meltdown chord (M+E+L+T held) — an intensified, screen-wide sag on top of
    // whatever the individual keys are already doing.
    if (uComboMeltdown > 0.0) {
      float md = uComboMeltdown;
      float drip = hash2(vec2(floor(uv.x * 55.0), 2.0));
      uv.y += 0.16 * md * drip * (0.5 + 0.5 * sin(T * 1.4 + uv.x * 30.0));
      uv.x += 0.02 * md * sin(uv.y * 18.0 + T * 1.1);
    }

    // Crystallize — snap uv to Voronoi cell centres for flat stained-glass facets;
    // modifier sets the facet count.
    if (uCrystallize > 0.0) {
      float cells = mix(6.0, 48.0, uModCrystallize);
      vec2 su = uv * cells;
      vec2 ip = floor(su);
      vec2 fp = fract(su);
      float best = 1e9;
      vec2 bestPoint = su;
      for (int y = -1; y <= 1; y++) {
        for (int x = -1; x <= 1; x++) {
          vec2 g = vec2(float(x), float(y));
          vec2 o = vec2(hash2(ip + g), hash2(ip + g + vec2(17.0, 3.0)));
          vec2 pnt = g + o;
          float dd = dot(pnt - fp, pnt - fp);
          if (dd < best) { best = dd; bestPoint = ip + pnt; }
        }
      }
      uv = mix(uv, bestPoint / cells, min(uCrystallize, 1.0));
    }

    float split = 0.0;
    if (uGlitch > 0.0) {
      float g = mix(0.3, 2.2, uModGlitch);
      float band = floor(uv.y * 24.0);
      float tt = floor(uTime * 12.0);
      float glitching = step(0.6, hash2(vec2(band, tt + 3.0)));
      uv.x += (hash2(vec2(band, tt)) - 0.5) * 0.15 * uGlitch * g * glitching;
      split = 0.02 * uGlitch * g;
    }

    // Pixellate — quantize the final (warped) coordinate into blocks; modifier
    // makes it chunkier. Placed last so every warp above blocks up together.
    if (uPixellate > 0.0) {
      float blocks = mix(300.0, 16.0, uModPixellate);
      uv = mix(uv, (floor(uv * blocks) + 0.5) / blocks, min(uPixellate, 1.0));
    }

    // Chroma RGB split now comes only from glitch (the chroma dial/letter are gone).
    float chroma = split;
    vec3 col;
    if (chroma > 0.0) {
      col.r = sampleField(uv + vec2(chroma, 0.0)).r;
      col.g = sampleField(uv).g;
      col.b = sampleField(uv - vec2(chroma, 0.0)).b;
    } else {
      col = sampleField(uv);
    }

    if (uUnderwater > 0.0) {
      // cheap blur: blend a couple of neighbour taps
      vec3 blur = sampleField(uv + vec2(0.004, 0.0)) + sampleField(uv - vec2(0.0, 0.004));
      col = mix(col, (col + blur) / 3.0, 0.6 * uUnderwater);
    }

    if (uInvert > 0.0) col = mix(col, 1.0 - col, min(uInvert, 1.0) * mix(0.5, 1.0, uModInvert));
    if (uAlien > 0.0) {
      float spread = mix(1.0, 3.5, uModAlien);
      vec3 a = 0.5 + 0.5 * cos(6.28318 * (col * spread + vec3(0.0, 2.0, 4.0)) + T);
      col = mix(col, a, min(uAlien, 1.0));
    }
    if (uXray > 0.0) {
      float c0 = luma(sampleField(uv));
      float e = abs(luma(sampleField(uv + vec2(0.0035, 0.0))) - c0)
              + abs(luma(sampleField(uv + vec2(0.0, 0.0035))) - c0);
      col = mix(col, vec3(smoothstep(0.0, mix(0.2, 0.05, uModXray), e * 6.0)), min(uXray, 1.0));
    }
    if (uHue > 0.0) col = hueRotate(col, uHue * mix(1.0, 5.0, uModHue) * (0.5 + uTime * 0.2));
    if (uNoise > 0.0) {
      float n = hash2(uv * uResolution.xy * 0.5 + fract(uTime));
      col += (n - 0.5) * mix(0.12, 0.9, uModNoise) * uNoise;
    }

    // Ocean — underwater caustic light woven over the frame; modifier = caustic scale.
    if (uOcean > 0.0) {
      vec2 p = aspect(uv) * mix(3.0, 9.0, uModOcean);
      float c = 0.0;
      vec2 q = p;
      for (int i = 0; i < 3; i++) {
        q += vec2(sin(q.y * 1.4 + T * 0.7), cos(q.x * 1.4 - T * 0.6));
        c += abs(sin(q.x) + cos(q.y));
      }
      c = pow(c / 6.0, 3.0);
      vec3 caust = mix(vec3(0.0, 0.15, 0.3), vec3(0.4, 0.9, 1.0), c);
      col = mix(col, col * 0.55 + caust, min(uOcean, 1.0));
    }

    // Lens Flare — a roaming light with a halo, anamorphic rays and ghost discs;
    // modifier sets ray count + intensity. Anchored in screen space (vUv), not warped.
    if (uLensflare > 0.0) {
      vec2 p = aspect(vUv);
      vec2 lp = vec2(sin(T * 0.3) * 0.45, cos(T * 0.23) * 0.28);
      vec2 d = p - lp;
      float dist = length(d);
      float halo = (0.015 + 0.04 * uModLensflare) / (dist * dist + 0.015);
      float rays = 0.5 + 0.5 * sin(atan(d.y, d.x) * mix(6.0, 18.0, uModLensflare));
      float ghosts = 0.0;
      for (int i = 1; i <= 3; i++) {
        vec2 gp = lp * (1.0 - float(i) * 0.55);
        ghosts += 0.015 / (dot(p - gp, p - gp) + 0.008);
      }
      vec3 add = palette(0.08 + dist * 0.6) * halo * (0.55 + 0.45 * rays)
               + vec3(1.0, 0.92, 0.75) * ghosts * 0.12;
      col += add * min(uLensflare, 1.0);
    }

    if (uBloom > 0.0) {
      vec3 glow = vec3(0.0);
      float rad = 0.006 * mix(0.5, 2.0, uModBloom);
      for (int i = 0; i < 6; i++) {
        float ang = float(i) * 1.0472;
        glow += max(sampleBase(uv + vec2(cos(ang), sin(ang)) * rad, BT) - 0.5, 0.0);
      }
      col += (glow / 6.0) * uBloom * mix(2.0, 4.5, uModBloom) * (0.5 + 0.5 * uBeat);
    }

    if (uVignette > 0.0) {
      float dd = distance(base, vec2(0.5));
      col *= mix(1.0, smoothstep(mix(0.95, 0.40, uModVignette), 0.15, dd), min(uVignette, 1.0));
    }

    // Strobe — latched (toggle) flash locked to the beat; modifier = flash depth.
    if (uStrobe > 0.0) {
      float depth = min(uStrobe, 1.0) * mix(0.6, 1.0, uModStrobe);
      col *= mix(1.0 - depth, 1.0, uBeat);
    }
    if (uLive > 0.5) col *= 0.55 + 0.45 * uBeat;

    // Meltdown chord — a hot, molten tint that sinks the frame while held.
    if (uComboMeltdown > 0.0) {
      float md = uComboMeltdown;
      vec3 molten = col * vec3(1.5, 0.72, 0.42) + vec3(0.06, 0.0, 0.0);
      col = mix(col, molten, 0.55 * md);
      col *= 1.0 + 0.18 * md * sin(uTime * 3.0);
    }

    // GLYPH sequence — a one-off chromatic supernova: a spinning hue sweep plus a
    // rainbow ring blooming out of the centre for the duration of the flash.
    if (uComboGlyph > 0.0) {
      float g = uComboGlyph;
      vec2 pc = base - 0.5;
      pc.x *= uResolution.x / max(uResolution.y, 1.0);
      float rr = length(pc);
      float ang = atan(pc.y, pc.x);
      float ring = 0.5 + 0.5 * sin(rr * 34.0 - uTime * 7.0);
      vec3 rainbow = palette(rr * 1.6 - uTime * 0.6 + ang * 0.16);
      col = mix(col, hueRotate(col, uTime * 3.5), g);
      col += rainbow * (0.30 + 0.45 * ring) * g * smoothstep(0.95, 0.1, rr);
      col *= 1.0 + 0.45 * g;
    }

    // Blackout / Whiteout — full-frame washes applied last so they override every
    // other effect; the modifier is the fade depth (default 1 = fully covered, so
    // the meter % matches the coverage, and +/- eases it back toward the scene).
    if (uBlackout > 0.0) col = mix(col, vec3(0.0), min(uBlackout, 1.0) * uModBlackout);
    if (uWhiteout > 0.0) col = mix(col, vec3(1.0), min(uWhiteout, 1.0) * uModWhiteout);

    gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  }
`;
